import logger from 'src/logger';
import { makeVideoRenderer, videoButtonType, videoRendererType } from 'src/sdk-components';
import { TourVideo, VSInteractionType } from 'src/sdk-components/VSObject';
import { SceneMode } from 'src/service/scene/scene-nodes/SceneImage/types';
import { TransformationMode } from 'src/service/scene/scene-nodes/TransformControls/data';
import { LoadingState, TSceneNode, VideoConfigType } from 'src/service/scene/types';
import { SDKConfig } from 'src/service/types';
import { MpSdk } from 'src/types';
import { getVersion } from 'src/utils/all';
import { NodeEventsTypes } from '../../enums';
import Scene from '../../scene';

class SceneVideo extends Scene {
  readonly mpSDK: any;

  assets: Array<any> = [];

  constructor(
    mpSDK: MpSdk,
    private config: SDKConfig,
    private transformControls: any,
  ) {
    super(mpSDK);
    this.mpSDK = mpSDK;
  }

  async init() {
    await this.mpSDK.Scene.register(videoRendererType, makeVideoRenderer);
  }

  async create(data: TourVideo, options: VideoConfigType): Promise<TSceneNode | undefined> {
    return new Promise(async (resolve) => {
      try {
        const assetNode = this.getAssetNode(data);
        if (assetNode) {
          resolve(assetNode);
          return;
        }

        // saving the asset into the array to prevent rendering it few times
        const assetsLength = this.assets.push([data, []]);
        const modelNode = await this.mpSDK.Scene.createNode();
        const component = modelNode.addComponent(videoRendererType, {
          autoplay: data.autoplay,
          isDefaultMuted: data.isDefaultMuted,
          greenScreenEnabled: data.details.greenScreenEnabled,
          greenScreenColor: data.details.greenScreenColor,
          greenScreenStrength: data.greenScreenStrength,
          greenScreenSmoothness: data.greenScreenSmoothness,
          greenScreenSpill: data.greenScreenSpill,
          enableHoverAnimation: data.enableHoverAnimation,
          animationPulse: data.animationPulse,
          animationUpDown: data.animationUpDown,
          animationFlip: data.animationFlip,
          adminPanelAccess: this.config.adminPanelAccess,
          loop: data.loop,
          hasExternalController: data.hasExternalController,
          hideOnAnimationFinished: data.hideOnAnimationFinished,
        });

        let playBtnComponent: any;
        let muteBtnComponent: any;
        if (options?.withButton && !data.hasExternalController) {
          const { mute, unMute, play, pause } = options.withButton;
          const [width, height] = component.getSize();
          const halfW = width * 0.5;
          const halfH = height * 0.5;
          const muteBtnSize = 0.05;
          const playBtnSize = 0.08;
          const muteBtnOffset = muteBtnSize + muteBtnSize * 0.5;
          const playBtnOffset = playBtnSize + playBtnSize * 0.5;

          muteBtnComponent = modelNode.addComponent(videoButtonType, {
            type: 'mute',
            size: !options.hideMuteButton ? muteBtnSize : 0,
            src: mute,
            src2: unMute,
            localPosition: {
              x: halfW - muteBtnOffset,
              y: -halfH + muteBtnSize + halfH * 0.05,
              z: 0.005,
            },
          });

          muteBtnComponent.onEvent = (type: any, eventData: any) => {
            if (type === VSInteractionType.CLICK) {
              this.emit(NodeEventsTypes.VIDEO_BUTTON_CLICK, {
                assetId: data.id,
                eventData,
                data,
                component,
                btnComponent: muteBtnComponent,
              });
            }
            if (type === VSInteractionType.HOVER) {
              if (eventData.hover) {
                if (muteBtnComponent.button) muteBtnComponent.button.visible = true;
                if (playBtnComponent?.button) playBtnComponent.button.visible = true;
              }
              this.emit(NodeEventsTypes.VIDEO_BUTTON_HOVER, {
                assetId: data.id,
                eventData,
                data,
              });
            }
          };

          if (play && pause) {
            playBtnComponent = modelNode.addComponent(videoButtonType, {
              type: 'play',
              size: playBtnSize,
              src: play,
              src2: pause,
              localPosition: {
                x: data.autoplay ? halfW - playBtnOffset : 0,
                y: data.autoplay ? -halfH + playBtnSize : 0,
                z: 0.005,
              },
            });

            playBtnComponent.onEvent = (type: any, eventData: any) => {
              if (type === VSInteractionType.HOVER) {
                if (!data.autoplay && playBtnComponent.button) {
                  playBtnComponent.button.visible = true;
                }
                if (muteBtnComponent.button) muteBtnComponent.button.visible = true;
                this.emit(NodeEventsTypes.VIDEO_BUTTON_HOVER, {
                  assetId: data.id,
                  eventData,
                  data,
                });
              }

              if (type === VSInteractionType.CLICK) {
                this.emit(NodeEventsTypes.VIDEO_BUTTON_CLICK, {
                  assetId: data.id,
                  eventData,
                  data,
                  component,
                  btnComponent: playBtnComponent,
                });
              }
            };
          }

          if (muteBtnComponent) {
            component.destroyButton = muteBtnComponent.onDestroy.bind(muteBtnComponent);
            component.muteBtn = muteBtnComponent;
          }
          if (playBtnComponent) component.playBtn = playBtnComponent;

          this.emit(NodeEventsTypes.UNMUTE_AFTER_START, {
            assetId: data.id,
            eventData: {},
            data,
            component,
          });
        }

        component.onEvent = async (type: string, eventData: any) => {
          if (type === VSInteractionType.HOVER) {
            component.toggleHoverAnimation(eventData.hover);
            this.emit(NodeEventsTypes.VIDEO_HOVER, { assetId: data.id, eventData, data });
            if (!data.autoplay && playBtnComponent?.button) {
              if (eventData.hover) {
                playBtnComponent.button.visible = true;
              } else {
                playBtnComponent.button.visible = data.node?.video?.paused;
              }
            }
            if (muteBtnComponent?.button && data.autoplay) {
              muteBtnComponent.button.visible = true;
            }
          }
          if (type === VSInteractionType.CLICK) {
            this.emit(NodeEventsTypes.VIDEO_CLICK, {
              assetId: data.id,
              eventData,
              data,
              component,
              btnComponent: playBtnComponent ? playBtnComponent : null,
            });
            // component.events[VSInteractionType.DRAG] = true;

            if (data.effectOpacity) {
              modelNode.obj3D.traverse((obj) => {
                if (!obj.isMesh) return;

                if (Array.isArray(obj.material)) {
                  for (let i = 0; i < obj.material.length; i += 1) {
                    obj.material[i].transparent = true;
                    obj.material[i].opacity = 0.5;
                  }
                } else {
                  obj.material.transparent = true;
                  obj.material.opacity = 0.5;
                }
              });
            } else if (data.effectStopAnimation) {
              component.stopAnimation();
            }
            // moving this to public repo
            // else if (data.effectHide) {
            //   // modelNode.stop();
            // }
          }
        };

        modelNode.obj3D.position.set(data.positionX, data.positionY, data.positionZ);
        modelNode.obj3D.rotation.set(data.rotationX, data.rotationY, data.rotationZ);
        modelNode.obj3D.scale.set(data.scaleX, data.scaleY, 0.1);
        modelNode.start();

        component.inputs.src = options?.vimeoURL ? options.vimeoURL : this.getURL(data);

        component.outputs.onPropertyChanged('loadingState', (state: any) => {
          if (state === LoadingState.Loaded) {
            ['pause', 'ended', 'volumechange'].forEach((event) => {
              component.video.addEventListener(event, () => {
                // We need only the mute event from 'volumechange'
                if (event === 'volumechange' && !component.video.muted) return;

                options?.onSoundStop?.();
              });
            });
            this.assets[assetsLength - 1] = [data, [modelNode, component]];
            if (component.canPlay) resolve([modelNode, component]);
            else
              component.video.addEventListener('canplaythrough', () => {
                resolve([modelNode, component]);
              });
            component.video.addEventListener('error', (error) => {
              console.error(error);
              resolve([] as any);
            });
          } else if (state === LoadingState.Error) {
            resolve([] as any);
          }
        });

        // immediately change mode to 'edit' for the new asset
        if (!data.id) {
          await this.changeMode(SceneMode.Edit, [modelNode, component]);
          // position of the new asset is the camera position
          // move 1 meter out from the user camera position
          modelNode.position.setX(modelNode.position.x + 1);
        }
      } catch (e) {
        logger.error(e);
        resolve([] as any);
      }
    });
  }

  remove(data: TourVideo) {
    const node = this.getAssetNode(data);
    if (node) {
      const [modelNode] = node;
      if (!modelNode) return;
      modelNode.stop();

      // cleaning up the assets array
      this.assets = this.filterAssetOut(data);
    }
  }

  private getURL(data: TourVideo) {
    if (!data.details || !data.details.url) return '';
    let url = `${this.config.appAws}${data.details.url}${getVersion(new Date())}`;
    if (data.details.url.includes('http')) {
      url = data.details.url;
    }
    return url;
  }

  async changeMode(
    mode: SceneMode,
    node: TSceneNode,
    transformationMode: TransformationMode = TransformationMode.TRANSLATE,
  ) {
    // TODO: remove code duplication
    const [modelNode, component] = node;
    if (!component.inputs.greenScreenEnabled)
      this.setNodeEditView(mode === SceneMode.Edit, component);

    if (mode === SceneMode.Edit) {
      // Make the transform control visible so that the user can manipulate the control selection.
      this.transformControls.control.outputs.objectRoot.visible = true;
      // The three.js object being controlled by this component.
      this.transformControls.control.inputs.selection = modelNode;
      // The transformation mode. Valid values are ‘translate’, ‘rotate’ and ‘scale’.
      this.transformControls.control.inputs.mode = transformationMode;

      // stopping the animation to let user change parameters
      component.stopAnimation();
    } else {
      this.transformControls.control.outputs.objectRoot.visible = false;
      this.transformControls.control.inputs.selection = null;
      this.transformControls.control.inputs.mode = transformationMode;

      component.playAnimation();
    }
  }

  setNodeEditView = (state = false, component: any) => {
    /** set state to true to render active component as a wireframe (green color for GLTF models) */

    if (!component) return;
    if (component.outputs.loadingState !== LoadingState.Loaded) return;

    component.outputs.objectRoot?.traverse((obj: THREE.Object3D) => {
      if (obj.type !== 'Mesh') return;
      // check if this is a single material or an array first, depends on the obj
      const mesh = obj as THREE.Mesh;

      const addWireframe = (material: any) => {
        material.transparent = true;
        material.opacity = 0.8;
      };
      const removeWireframe = (material: any) => {
        material.transparent = false;
        material.opacity = 1;
      };

      const wireframeFunction = state ? addWireframe : removeWireframe;

      if (Array.isArray(mesh.material)) {
        for (let i = 0; i < mesh.material.length; i++) {
          wireframeFunction(mesh.material[i]);
        }
      } else {
        wireframeFunction(mesh.material);
      }
    });
  };

  getAssetNode = (asset: any): TSceneNode | null => {
    let node = null;
    this.assets.forEach(([a, n]) => {
      if ((a.id && a.id === asset.id) || (a.uuid === asset.uuid && a.uuid && asset.uuid)) {
        node = n;
      }
    });
    return node;
  };

  filterAssetOut = (asset: any): Array<[any, any[]]> => {
    return this.assets.filter(
      ([a]) => !((a.id && a.id === asset.id) || (a.uuid === asset.uuid && a.uuid && asset.uuid)),
    );
  };
}

export default SceneVideo;
